import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  increment,
  query,
  where,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyATxknPDo0pPNcu1SJlLh2MoFPTerju2Yc",
  authDomain: "tracksy-1dd4e.firebaseapp.com",
  projectId: "tracksy-1dd4e",
  storageBucket: "tracksy-1dd4e.firebasestorage.app",
  messagingSenderId: "160031916997",
  appId: "1:160031916997:web:32897de42b1afc099301ac",
  measurementId: "G-XWVGGXZQNQ"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();

// Auth helpers
export const loginWithEmail = (email, password) => 
  signInWithEmailAndPassword(auth, email, password);

export const signupWithEmail = (email, password) => 
  createUserWithEmailAndPassword(auth, email, password);

export const loginWithGoogle = () => 
  signInWithPopup(auth, googleProvider);

export const logout = () => signOut(auth);

export const resetPassword = (email) => 
  sendPasswordResetEmail(auth, email);

// Firestore helpers
export const getUserData = async (uid) => {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

export const saveUserData = async (uid, data) => {
  const docRef = doc(db, 'users', uid);
  await setDoc(docRef, { ...data, updatedAt: new Date().toISOString() }, { merge: true });
};

export const subscribeToUserData = (uid, callback) => {
  const docRef = doc(db, 'users', uid);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    }
  });
};

// Admin exemption check — reads config/exemptions AND config/proWhitelist from Firestore.
// Returns the union of both lists (paywall bypass for any email in either).
export const getExemptEmails = async () => {
  try {
    const [exemptSnap, whitelistSnap] = await Promise.all([
      getDoc(doc(db, 'config', 'exemptions')),
      getDoc(doc(db, 'config', 'proWhitelist')),
    ]);
    const exempt = exemptSnap.exists() ? (exemptSnap.data().emails || []) : [];
    const whitelist = whitelistSnap.exists() ? (whitelistSnap.data().emails || []) : [];
    return [...new Set([...exempt, ...whitelist])];
  } catch {
    return [];
  }
};

// Voice memo subcollection helpers (no Firebase Storage required)
export const saveVoiceMemo = async (uid, memoId, data) => {
  const docRef = doc(db, 'users', uid, 'voiceMemos', memoId);
  await setDoc(docRef, data);
};

export const deleteVoiceMemo = async (uid, memoId) => {
  const docRef = doc(db, 'users', uid, 'voiceMemos', memoId);
  await deleteDoc(docRef);
};

export const getVoiceMemos = async (uid) => {
  const colRef = collection(db, 'users', uid, 'voiceMemos');
  const snap = await getDocs(colRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── proWhitelist ────────────────────────────────────────────────────────────
export const getProWhitelist = async () => {
  try {
    const docSnap = await getDoc(doc(db, 'config', 'proWhitelist'));
    return docSnap.exists() ? (docSnap.data().emails || []) : [];
  } catch {
    return [];
  }
};

// ─── Plan field helpers ───────────────────────────────────────────────────────
export const setPlanInFirestore = async (uid, plan, stripeData = {}) => {
  const docRef = doc(db, 'users', uid);
  await setDoc(docRef, {
    plan,
    ...stripeData,
    planUpdatedAt: new Date().toISOString(),
  }, { merge: true });
};

// ─── AI usage (per-month counter in subcollection) ───────────────────────────
const aiUsageKey = (year, month) => `${year}-${String(month + 1).padStart(2, '0')}`;

export const getAiUsage = async (uid) => {
  const now = new Date();
  const key = aiUsageKey(now.getFullYear(), now.getMonth());
  try {
    const docSnap = await getDoc(doc(db, 'users', uid, 'aiUsage', key));
    return docSnap.exists() ? (docSnap.data().count || 0) : 0;
  } catch {
    return 0;
  }
};

export const incrementAiUsage = async (uid) => {
  const now = new Date();
  const key = aiUsageKey(now.getFullYear(), now.getMonth());
  const docRef = doc(db, 'users', uid, 'aiUsage', key);
  try {
    await setDoc(docRef, { count: increment(1) }, { merge: true });
  } catch {
    // fail silently — usage tracking should never block sending
  }
};

// ─── Voice memo helpers with timestamps ──────────────────────────────────────
export const getVoiceMemoCount = async (uid) => {
  try {
    const colRef = collection(db, 'users', uid, 'voiceMemos');
    const snap = await getDocs(colRef);
    return snap.size;
  } catch {
    return 0;
  }
};

export const updateVoiceMemoTranscription = async (uid, memoId, transcription) => {
  try {
    const docRef = doc(db, 'users', uid, 'voiceMemos', memoId);
    await setDoc(docRef, { transcription }, { merge: true });
  } catch {
    // fail silently
  }
};

export { onAuthStateChanged };
